import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { DocumentMeasurement } from "./document-measurement.types";

@Injectable({
    providedIn: 'root'
})
export class DocumentMeasurementsService {
    // Private
    private _documentMeasurement: BehaviorSubject<DocumentMeasurement | null> = new BehaviorSubject(null);
    private _documentMeasurements: BehaviorSubject<DocumentMeasurement[] | null> = new BehaviorSubject(null);

    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for documentMeasurement
     */
    get documentMeasurement$(): Observable<DocumentMeasurement> {
        return this._documentMeasurement.asObservable();
    }

    /**
     * Getter for documentMeasurements
     */
    get documentMeasurements$(): Observable<DocumentMeasurement[]> {
        return this._documentMeasurements.asObservable();
    }

    /**
     * Get documentMeasurements
     */
    getDocumentMeasurements(reportId: number): Observable<BaseRespone<DocumentMeasurement[]>> {
        return this._httpClient.get<BaseRespone<DocumentMeasurement[]>>(`${environment.baseUrl}/Report/GetDocumentMeasurementlist/${reportId}`).pipe(
            tap((response) => {
                this._documentMeasurements.next(response.result);
            })
        );
    }

    addNewMeasurement(reportId: number): void {
        const currentMeasurements = this._documentMeasurements.getValue();

        const newMeasurement = {
            id: 0,
            orderIndex: currentMeasurements.length + 1,
            flatPlotNo: '',
            carpetArea: 0,
            builtUpArea: 0,
            superBuiltUpArea: 0.0,
            reportId: reportId,
            deedArea: 0,
            deedBuiltUp: 0,
            deedSuperBuiltUp: 0,
            selected: false,
            isEditing: true
        };

        this._documentMeasurements.next([...currentMeasurements, newMeasurement]);
    }

    cancelDocumentRow(index: number): void {
    const measurements = this._documentMeasurements.getValue();

    if (!measurements[index].flatPlotNo?.trim()) {
        // If flatPlotNo is empty, remove the row
        measurements.splice(index, 1);
    } else {
        // Otherwise, set isEditing to false
        measurements[index].isEditing = false;
    }

    this._documentMeasurements.next([...measurements]);
}



    /**
      * Create documentMeasurement
      */
    createDocumentMeasurement(documentMeasurement: DocumentMeasurement): Observable<BaseRespone<DocumentMeasurement>> {
        return this.documentMeasurements$.pipe(
            take(1),
            switchMap(documentMeasurements => this._httpClient.post<BaseRespone<DocumentMeasurement>>(`${environment.baseUrl}/Report/AddDocumentMeasurement`, documentMeasurement)
                .pipe(
                    catchError((error) => {
                        return of(error.error);
                    }),
                    map((response) => {
                        // Return the new product
                        if (response.isSuccess) {
                            const currentMeasurements = this._documentMeasurements.getValue();

                            // Combine new result with existing values
                            const combined = [response.result, ...currentMeasurements];

                            // Filter out items where id === 0
                            const filtered = combined.filter(item => item.id !== 0);

                            // Update BehaviorSubject with filtered array
                            this._documentMeasurements.next(filtered);
                        }
                        return response;
                    })
                ))
        );
    }

    /**
     * Get board
     *
     * @param id
     */
    getDocumentMeasurement(id: number): Observable<BaseRespone<DocumentMeasurement>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetDocumentMeasurement/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._documentMeasurement.next(board.result))
        );
    }


    /**
    * Get contact by id
    */
    getDocumentMeasurementById(id: number): Observable<DocumentMeasurement> {
        return this._documentMeasurements.pipe(
            take(1),
            map((documentMeasurements) => {

                // Find the contact
                const documentMeasurement = documentMeasurements.find(item => item.id === id) || null;

                // Update the documentMeasurement
                this._documentMeasurement.next(documentMeasurement);

                // Return the documentMeasurement
                return documentMeasurement;
            }),
            switchMap((documentMeasurement) => {

                if (!documentMeasurement) {
                    return throwError('Could not found documentMeasurement with id of ' + id + '!');
                }

                return of(documentMeasurement);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateDocumentMeasurement(documentMeasurement: DocumentMeasurement): Observable<BaseRespone<DocumentMeasurement>> {
        return this.documentMeasurements$.pipe(
            take(1),
            switchMap(documentMeasurements => this._httpClient.put<BaseRespone<DocumentMeasurement>>(`${environment.baseUrl}/Report/UpdateDocumentMeasurement`, documentMeasurement).pipe(
                map((documentMeasurementCard) => {
                    const index = documentMeasurements.findIndex(item => item.id === documentMeasurement.id);

                    // Update the product
                    if (documentMeasurementCard.isSuccess) {
                        documentMeasurements[index] = documentMeasurementCard.result;

                        // Update the products
                        this._documentMeasurements.next(documentMeasurements);

                        this._documentMeasurement.next(documentMeasurementCard.result);
                    }
                    // Return the updated card
                    return documentMeasurementCard;
                }), catchError((error) => {
                    return of(error.error);
                })
            ))
        );
    }

    deleteDocumentMeasurement(id: number): Observable<BaseRespone<boolean>> {
        return this.documentMeasurements$.pipe(
            take(1),
            switchMap(documentMeasurements => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteDocumentMeasurement/${id}`).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    // Find the index of the deleted product
                    const index = documentMeasurements.findIndex(item => item.id === id);

                    // Delete the product
                    documentMeasurements.splice(index, 1);

                    // Update the products
                    this._documentMeasurements.next(documentMeasurements);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );
    }


    deleteAllDocumentMeasurement(ids: number[]): Observable<BaseRespone<boolean>> {
        const requestBody = { ids: ids };

        return this.documentMeasurements$.pipe(
            take(1),
            switchMap(documentMeasurements => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteAllDocumentMeasurements`, {
                body: requestBody
            }).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    ids.forEach(id => {
                        // Find the index of the deleted product
                        const index = documentMeasurements.findIndex(item => item.id === id);
                        // Delete the product
                        documentMeasurements.splice(index, 1);
                    });
                    // Update the products
                    this._documentMeasurements.next(documentMeasurements);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );

    }

}
